<template>
  <div class="chart-card">
    <div class="chart-header">
      <h4>OS Distribution</h4>
      <div class="info-tooltip">
        <span class="info-icon">ℹ️</span>
        <div class="tooltip-content">
          Shows the breakdown of operating systems across all devices in inventory. Helps understand platform distribution.
        </div>
      </div>
    </div>
    <div class="pie-chart">
      <div v-for="os in osDistribution" :key="os.name" class="pie-item">
        <div class="pie-color" :style="{ backgroundColor: os.color }"></div>
        <span>{{ os.name }}: {{ os.percentage }}% ({{ os.count }})</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { COLORS } from '@/styles/brand'

const osDistribution = ref([])

const props = defineProps({
  devices: Array
})

const calculateOSDistribution = () => {
  const osCounts = {}
  props.devices.forEach(device => {
    const os = device.os || 'Unknown'
    osCounts[os] = (osCounts[os] || 0) + 1
  })
  
  const total = props.devices.length
  const colors = ['#e53e3e', '#38a169', '#3182ce', '#805ad5', '#d69e2e']
  
  osDistribution.value = Object.entries(osCounts)
    .map(([name, count], index) => ({
      name,
      count,
      percentage: Math.round((count / total) * 100),
      color: colors[index % colors.length]
    }))
}

watch(() => props.devices, () => {
  if (props.devices) {
    calculateOSDistribution()
  }
}, { immediate: true })
</script>

<style scoped>
.chart-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.1) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  border-radius: 16px;
  position: relative;
  overflow: visible;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.chart-header h4 {
  color: v-bind('COLORS.PRIMARY.WHITE');
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.info-tooltip {
  position: relative;
  display: inline-block;
}

.info-icon {
  cursor: help;
  font-size: 1rem;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.info-icon:hover {
  opacity: 1;
}

.tooltip-content {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.95);
  color: v-bind('COLORS.PRIMARY.WHITE');
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  width: 280px;
  z-index: 9999;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.tooltip-content::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 12px;
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.95);
}

.info-tooltip:hover .tooltip-content {
  visibility: visible;
  opacity: 1;
}

.pie-item {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.pie-color {
  width: 16px;
  height: 16px;
  border-radius: 50%;
}
</style>